import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { TabContext } from '../../Common/Form/Tab/TabContext';
import { object } from 'yup';
import { Button, TextField, ErrorMessage, extractErrors, validationRules } from '../../Common/Form';

const { firstName, lastName, nickname, memberTitle, phone } = validationRules;

const schema = object({
    firstName,
    lastName,
    nickname,
    jobTitle: memberTitle,
    phone,
});

const StepTwo = ({ event, user, setUser, vendorShare, setVendorShare, onSubmit, globalError, hasSessions }) => {
    const { tabIndex } = useContext(TabContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const updateUser = useCallback((field, value) => setUser((prev) => ({ ...prev, [field]: value })), [setUser]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        schema
            .validate(user, { strict: true, abortEarly: false })
            .then(() => {
                setErrors({});
                onSubmit();
                setIsSubmitting(false);
            })
            .catch((errs) => {
                setErrors(extractErrors(errs));
                setIsSubmitting(false);
            });
    };

    if (!user) {
        return null;
    }

    return (
        <form onSubmit={handleSubmit}>
            <div>
                {!!hasSessions && <span className="caps">Step 2 of 2</span>}
                <h3 className="registration__heading">Member Profile Information</h3>
                <p>
                    The information below is part of your membership profile. Please verify that it is correct and then
                    click Complete My Registration. Any information you update here will automatically update your
                    membership profile.
                </p>
                <fieldset className="grid -col3">
                    <div className="text grid__cell">
                        <TextField
                            id="firstName"
                            label="First Name"
                            managed
                            value={user.firstName}
                            onChange={(e) => updateUser(e.target.name, e.target.value)}
                            required
                        />
                        <ErrorMessage message={errors.firstName ?? ''} />
                    </div>
                    <div className="text grid__cell">
                        <TextField
                            id="lastName"
                            label="Last Name"
                            managed
                            value={user.lastName}
                            onChange={(e) => updateUser(e.target.name, e.target.value)}
                            required
                        />
                        <ErrorMessage message={errors.lastName ?? ''} />
                    </div>
                    <div className="text grid__cell">
                        <TextField
                            id="nickname"
                            label="Nickname"
                            managed
                            value={user.nickname ?? ''}
                            onChange={(e) => updateUser(e.target.name, e.target.value)}
                            required={false}
                        />
                        <ErrorMessage message={errors.nickname ?? ''} />
                    </div>
                </fieldset>

                <fieldset className="grid -col3">
                    <div className="grid__cell">
                        <TextField
                            id="account"
                            label="Account Name / Firm"
                            value={user.acct}
                            required={false}
                            disabled
                        />
                    </div>
                    <div className="grid__cell">
                        <TextField
                            id="jobTitle"
                            label="Title/Position"
                            managed
                            value={user.jobTitle ?? ''}
                            onChange={(e) => updateUser(e.target.name, e.target.value)}
                            required
                        />
                        <ErrorMessage message={errors.jobTitle ?? ''} />
                    </div>
                    <div className="grid__cell">
                        <TextField
                            id="phone"
                            label="Phone"
                            managed
                            value={user.phone ?? ''}
                            onChange={(e) => updateUser(e.target.name, e.target.value)}
                            required
                        />
                        <ErrorMessage message={errors.phone ?? ''} />
                    </div>

                    {event?.displayVendorShareCheckbox && (
                        <>
                            <h3 className="registration__heading">Connect With Cutter Partner(s)</h3>
                            <span style={{ marginBottom: '5px' }}>
                                By checking the box below, you agree to share your contact information with the
                                sponsor(s) of this event.
                            </span>
                            <div className="text grid__cell">
                                <label>
                                    <input
                                        className="checkbox"
                                        aria-label="Consent"
                                        title="Consent"
                                        type="checkbox"
                                        name="vendorShare"
                                        onChange={() => setVendorShare((prev) => !prev)}
                                        checked={vendorShare}
                                        disabled={event.registrationId}
                                    />
                                    Share my contact information
                                </label>
                            </div>
                        </>
                    )}
                </fieldset>

                <Button
                    text={`${event?.registrationId ? 'Update' : 'Complete'}  My Registration`}
                    disabled={isSubmitting}
                />

                {!!globalError && (
                    <div>
                        <p className="messaging -error">{globalError}</p>
                    </div>
                )}
            </div>
        </form>
    );
};

StepTwo.propTypes = {
    event: PropTypes.object,
    user: PropTypes.object,
    setUser: PropTypes.func.isRequired,
    vendorShare: PropTypes.bool,
    setVendorShare: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    hasSessions: PropTypes.bool.isRequired,
    globalError: PropTypes.string,
};

export default StepTwo;
